/**
 * useRecordExtValues — lokalny stan pól dodatkowych (ext) rekordu, zasilany WARTOŚCIĄ Z SERWERA
 * dołączoną do odpowiedzi GET rekordu (`record.extValues`) — bez osobnego żądania o pola
 * dodatkowe. Jeden hook dla stron kartotek i dokumentów (kontrahent, pracownik, firma,
 * użytkownik, kontakt kontrahenta, klasy dokumentów).
 *
 * ŻADNEGO żądania sieciowego w hooku — wartość dostarcza strona z danych własnego zapytania o
 * rekord (`undefined` w trakcie ładowania i w trybie create). Uprawnienie do odczytu sprawdza
 * już sam GET rekordu.
 *
 * Reguła synchronizacji:
 *  - Zmiana rekordu (`recordId`) ZAWSZE zaczyna od czystej karty: lokalne wartości poprzedniego
 *    rekordu nie mogą przeciec do nowego (ani trafić w jego PATCH), więc stan wraca do wartości
 *    serwera nowego rekordu (do `{}` aż dane dojdą). To obejmuje też create → zapisany rekord,
 *    gdy strona się nie remontuje. Reset dzieje się W TRAKCIE RENDERU (nie w efekcie), więc
 *    pierwsza zatwierdzona klatka po zmianie rekordu nie pokazuje wartości poprzedniego.
 *  - Ten sam rekord, nowy fetch (pierwsze załadowanie, odświeżenie po zapisie, odświeżenie w
 *    tle po zapisie z innej zakładki; wyzwala go zmiana `serverExtValues` ALBO `serverVersion` —
 *    ta druga łapie odświeżenie, które zwróciło dane identyczne z poprzednimi i dzięki structural
 *    sharing zachowało tę samą referencję): bierzemy wartość serwera, ALE pola ruszone
 *    przez użytkownika (`handleExtChange`) i jeszcze niezapisane zostają przy jego wartości —
 *    odświeżenie spoza formularza nie może cicho zgubić trwającej edycji (ta sama klasa błędu,
 *    którą wcześniej łatano guardem `editing && hydratedRecordIdRef === id` na stronach
 *    kontrahenta i pracownika). Pola nieruszone przejmują wartość serwera, więc zmiana innego
 *    pola zapisana osobno nie zostanie nadpisana starą wartością przy kolejnym zapisie.
 *  - Edycje przestają być „niezapisane" dopiero na jawne polecenie strony:
 *      `markSaved()`    — po udanym zapisie (wartości są już na serwerze; kolejna wartość
 *                         serwera, w tym znormalizowana przez backend, wchodzi bez zastrzeżeń),
 *      `discardEdits()` — po „Anuluj" (wyrzuca lokalne zmiany i wraca do wartości serwera).
 *    Wołamy je z akcji użytkownika / onSuccess mutacji, nie z efektu reagującego na tło zapytań.
 *  - Wyścig „edycja w trakcie zapisu": pola nie są blokowane na czas żądania, więc użytkownik
 *    może ruszyć pole B, gdy w locie jest zapis z polem A. Samo `markSaved()` czyszczące
 *    wszystko zgubiłoby wtedy edycję B (odświeżenie po zapisie nadpisałoby ją wartością serwera).
 *    Dlatego strona woła `snapshotEdits()` tuż przed włożeniem `extValues` do body (przed
 *    jakimkolwiek `await` w funkcji zapisu): `markSaved()`
 *    zdejmuje wtedy tylko te pola, których numer edycji nie zmienił się od migawki — pola
 *    ruszone po wysłaniu zostają „niezapisane". Bez migawki `markSaved()` czyści wszystko.
 *
 * Zapis jest atomowy GDZIE INDZIEJ (saveAggregate): ten hook tylko trzyma lokalny stan do
 * wysłania w body POST/PATCH (`extValues`) — nie ma tu żadnego PUT.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Stała referencja pustego stanu — stabilna dla `useMemo`/zależności po stronie stron. */
const EMPTY_EXT_VALUES: Record<string, unknown> = Object.freeze({});

/**
 * Czy bieżące wartości pól dodatkowych różnią się od wartości serwera? Dla stron, które wysyłają
 * PATCH tylko przy realnej zmianie (użytkownik: „nic się nie zmieniło" = brak żądania). Porównanie
 * per slug po TREŚCI (tablice multiselect mają nową referencję przy każdym renderze); `null` i
 * brak wartości są równoważne. Brak wartości serwera (`undefined`, np. rekord jeszcze się ładuje)
 * traktujemy jak `{}`.
 */
export function extValuesDiffer(
  current: Record<string, unknown>,
  server: Record<string, unknown> | undefined,
): boolean {
  const base = server ?? EMPTY_EXT_VALUES;
  const slugs = new Set([...Object.keys(current), ...Object.keys(base)]);
  for (const slug of slugs) {
    const a = current[slug] ?? null;
    const b = base[slug] ?? null;
    if (a === b) continue;
    if (JSON.stringify(a) !== JSON.stringify(b)) return true;
  }
  return false;
}

export interface UseRecordExtValuesOptions {
  /** Id rekordu z trasy; `undefined` w trybie create (rekord jeszcze nie istnieje). */
  recordId: string | undefined;
  /**
   * `extValues` z odpowiedzi serwera na GET rekordu (`record.extValues`); `undefined` w trakcie
   * ładowania i w trybie create.
   *
   * Referencja MUSI być stabilna między renderami, gdy dane się nie zmieniły (wartość z cache
   * zapytania, np. `detailQ.data?.extValues` — NIE nowy obiekt w każdym renderze, np. inline
   * `?? {}`): efekt synchronizacji reaguje na zmianę referencji, więc niestabilna groziłaby
   * pętlą renderów (efekt woła `setExtValues` przy każdej „nowej" wartości).
   */
  serverExtValues: Record<string, unknown> | undefined;
  /**
   * Znacznik „kolejny udany fetch rekordu" — strona podaje `dataUpdatedAt` swojego zapytania o
   * rekord. Potrzebny, bo gdy odświeżony rekord ma `extValues` równe poprzednim, structural
   * sharing TanStack Query zachowuje TĘ SAMĄ referencję i sama `serverExtValues` się nie zmienia
   * — a pole zostałoby wtedy przy wartości lokalnej, której serwer nie zapisał (np. odrzuconej
   * lub znormalizowanej do poprzedniej). Zmiana znacznika ponownie synchronizuje stan z serwerem.
   * Wymagany (nie opcjonalny): pominięty po cichu przywracałby błąd „pole zostaje przy wartości,
   * której serwer nie zapisał", a typy wyłapują to już przy kompilacji strony.
   */
  serverVersion: number;
}

export interface UseRecordExtValuesResult {
  /** Aktualne wartości pól dodatkowych (serwer scalony z niezapisanymi edycjami). */
  extValues: Record<string, unknown>;
  /** Zmiana jednego pola — scala w stan lokalny i oznacza pole jako niezapisane. */
  handleExtChange: (slug: string, value: unknown) => void;
  /**
   * Tuż PRZED wysłaniem body z `extValues`: zapamiętuje, które pola i w której wersji edycji
   * trafiają do żądania, żeby `markSaved()` nie zdjął edycji zrobionych w trakcie zapisu.
   * Wołać w tym samym synchronicznym kroku, w którym `extValues` trafia do body — przed
   * jakimkolwiek `await` w funkcji zapisu (edycja zrobiona podczas oczekiwania nie jedzie w body).
   */
  snapshotEdits: () => void;
  /**
   * Po udanym zapisie: edycje są już na serwerze — kolejna wartość serwera nadpisze stan.
   * Po `snapshotEdits()` zdejmuje tylko pola nieruszone od migawki; bez migawki — wszystkie.
   */
  markSaved: () => void;
  /** Po „Anuluj": wyrzuca niezapisane edycje i wraca do ostatniej wartości serwera. */
  discardEdits: () => void;
}

export function useRecordExtValues({
  recordId,
  serverExtValues,
  serverVersion,
}: UseRecordExtValuesOptions): UseRecordExtValuesResult {
  const [extValues, setExtValues] = useState<Record<string, unknown>>(
    () => serverExtValues ?? EMPTY_EXT_VALUES,
  );

  // Rekord, dla którego stan lokalny jest aktualnie zasilany z serwera — porównywany w TRAKCIE
  // renderu (wzorzec „poprzedni prop w stanie"), żeby przy zmianie rekordu nie mignęła ani
  // jedna klatka z wartościami poprzedniego (reset w efekcie dzieje się dopiero po malowaniu).
  const [syncedRecordId, setSyncedRecordId] = useState<string | undefined>(recordId);
  if (syncedRecordId !== recordId) {
    setSyncedRecordId(recordId);
    setExtValues(serverExtValues ?? EMPTY_EXT_VALUES);
  }

  // Rekord, do którego należy `editedSlugsRef` — przy jego zmianie efekt czyści zbiór (ref
  // zmieniamy w efekcie, nie w renderze).
  const editsOwnerIdRef = useRef<string | undefined>(recordId);
  // Pola ruszone przez użytkownika od ostatniego zapisu / anulowania / zmiany rekordu, z numerem
  // ostatniej edycji (rośnie z każdym `handleExtChange`) — dla `snapshotEdits` / `markSaved`.
  const editedSlugsRef = useRef<Map<string, number>>(new Map());
  const editCounterRef = useRef(0);
  // Kopia `editedSlugsRef` z chwili `snapshotEdits()` (migawka wysłanego zapisu); `null` = brak.
  const snapshotRef = useRef<Map<string, number> | null>(null);
  // Ostatnia znana wartość serwera DLA BIEŻĄCEGO rekordu — dla `discardEdits` (bez pułapki
  // starego domknięcia w callbacku wołanym z handlera strony). `undefined` (dane chwilowo
  // niedostępne) NIE zastępuje jej w obrębie tego samego rekordu.
  const serverRef = useRef<Record<string, unknown> | undefined>(serverExtValues);

  useEffect(() => {
    if (editsOwnerIdRef.current !== recordId) {
      // Zmiana rekordu: stan został już zresetowany w renderze (wyżej) — tu tylko porządki.
      // `undefined` jest tu poprawną wartością serwera (dane nowego rekordu jeszcze się ładują).
      editsOwnerIdRef.current = recordId;
      editedSlugsRef.current.clear();
      snapshotRef.current = null;
      serverRef.current = serverExtValues;
      return;
    }

    if (!serverExtValues) return;
    serverRef.current = serverExtValues;

    const edited = editedSlugsRef.current;
    if (edited.size === 0) {
      setExtValues(serverExtValues);
      return;
    }
    setExtValues((prev) => {
      const next: Record<string, unknown> = { ...serverExtValues };
      for (const slug of edited.keys()) {
        if (slug in prev) next[slug] = prev[slug];
      }
      return next;
    });
  }, [serverExtValues, recordId, serverVersion]);

  const handleExtChange = useCallback((slug: string, value: unknown) => {
    editCounterRef.current += 1;
    editedSlugsRef.current.set(slug, editCounterRef.current);
    setExtValues((prev) => ({ ...prev, [slug]: value }));
  }, []);

  const snapshotEdits = useCallback(() => {
    snapshotRef.current = new Map(editedSlugsRef.current);
  }, []);

  const markSaved = useCallback(() => {
    const snapshot = snapshotRef.current;
    snapshotRef.current = null;
    if (!snapshot) {
      editedSlugsRef.current.clear();
      return;
    }
    for (const [slug, seq] of snapshot) {
      if (editedSlugsRef.current.get(slug) === seq) editedSlugsRef.current.delete(slug);
    }
  }, []);

  const discardEdits = useCallback(() => {
    editedSlugsRef.current.clear();
    snapshotRef.current = null;
    setExtValues(serverRef.current ?? EMPTY_EXT_VALUES);
  }, []);

  return { extValues, handleExtChange, snapshotEdits, markSaved, discardEdits };
}
